import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { customerZ } from '@libs/schemas';
import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormLabel, FormControl, Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FC } from 'react';
import { Loader } from 'components/Loader';
import { trpc } from 'src/trpc';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { useToast } from 'components/useToast';
import { PhoneFormField } from 'components/ui/PhoneInput';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PHONE_REGEX, isValidPhoneNumber } from '@libs/schemas';

interface ConsentContextInput {
  timestamp: Date | string;
}

// Visible metadata is deliberately limited to when consent last changed and which admin changed
// it. Source, the raw actor id and the mobile number stay out of the UI: the number already has
// its own field on this form, and the raw id is not meaningful to an operator. None of that is
// dropped from storage, so marketingConsentHistory remains the full audit record.
export const formatConsentContext = (
  latest: ConsentContextInput,
  actorName?: string | null,
  // The visible line abbreviates the month; the accessible phrase spells it out, since screen
  // readers pronounce "Sep" inconsistently.
  monthStyle: 'short' | 'long' = 'short',
): string => {
  const timestamp = new Date(latest.timestamp).toLocaleDateString('en-US', {
    month: monthStyle,
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return actorName ? `Last updated ${timestamp} by ${actorName}` : `Last updated ${timestamp}`;
};

interface Props {
  onSuccess?: () => void;
  customerId?: string; // Optional prop for when not using useParams
}

// Use the existing customerZ schema but modify it to match adminUpdateCustomer requirements
const customerUpdateSchema = customerZ
  .omit({ smsNotificationOptIn: true, marketingSmsOptIn: true })
  .extend({
    email: z.email(),
    phone: z
      .string()
      .regex(PHONE_REGEX, 'Invalid phone number format. Must be +1 followed by 10 digits')
      .refine(isValidPhoneNumber, 'Please enter a valid US phone number'),
    marketingSmsOptIn: z.boolean().default(false),
  });

export const CustomerDetailsForm: FC<Props> = ({ onSuccess, customerId }) => {
  const params = useParams();
  const { errorToast } = useToast();
  const id = customerId || params.id!;
  const utils = trpc.useUtils();
  const { data: customer, isLoading: userInfoLoading } =
    trpc.admin.customer.adminGetCustomer.useQuery(id);
  const { mutate, isPending } = trpc.admin.customer.adminUpdateCustomer.useMutation({
    onSuccess: async () => {
      // The consent line reports the persisted event, so it has to come from a refetched
      // customer. Without this the card would keep describing the previous event after a save.
      await utils.admin.customer.adminGetCustomer.invalidate(id);
      onSuccess?.();
    },
    onError: (error: { message?: string }) => {
      errorToast({
        title: 'Failed to update customer',
        description:
          error.message || 'Please try again or contact support if the problem persists.',
      });
    },
  });

  const form = useForm({
    resolver: zodResolver(customerUpdateSchema),
    values: customer
      ? {
          email: customer.email || '',
          name: {
            first: customer.name?.first || '',
            last: customer.name?.last || '',
          },
          phone: customer.phone || '',
          company: customer.company || { name: undefined, email: undefined },
          marketingSmsOptIn: customer.marketingSmsOptIn ?? false,
        }
      : undefined,
  });

  if (userInfoLoading) {
    return <Loader title="Gathering your info" />;
  }

  if (isPending) {
    return <Loader title="Updating your info" />;
  }

  const history = customer?.marketingConsentHistory;
  const latestConsentEvent = history?.length ? history[history.length - 1] : undefined;
  // Status and timestamp stay tied to the saved consent rather than the live switch position.
  // Reading the toggle here would pair a just-flipped "Opted out" with the date the customer
  // opted in, describing an event that never happened until the save lands.
  const savedOptIn = customer?.marketingSmsOptIn ?? false;
  const savedStatusLabel = savedOptIn ? 'Opted in' : 'Opted out';
  const actorName = customer?.latestMarketingConsentActorName;
  const consentContext = latestConsentEvent
    ? formatConsentContext(latestConsentEvent, actorName)
    : undefined;
  const consentSpokenPhrase = latestConsentEvent
    ? `${savedStatusLabel}. ${formatConsentContext(latestConsentEvent, actorName, 'long')}.`
    : `${savedStatusLabel}.`;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((v) =>
          mutate({
            ...v,
            name: { first: v.name.first, last: v.name.last },
            customerId: id,
            userId: customer!.userId,
            email: v.email || '',
            phone: v.phone || '',
            company: {
              name: v.company?.name || undefined,
              email: v.company?.email || undefined,
            },
            // Consent travels only when an admin actually moved the toggle on this form. The
            // value seeded from the customer record can be stale - the customer may have
            // replied STOP, or withdrawn consent in their own account, since this form loaded -
            // and resending it on an unrelated profile save would silently re-subscribe them.
            // Omitting the field leaves the recorded consent exactly as it stands.
            marketingSmsOptIn: form.formState.dirtyFields.marketingSmsOptIn
              ? v.marketingSmsOptIn
              : undefined,
          }),
        )}
        className="h-full w-full flex flex-col flex-grow"
      >
        <div className="w-full justify-center flex flex-col gap-4 flex-grow justify-between">
          <p className="text-xl font-semibold">Account Details</p>
          <div className="grid grid-cols-2 gap-8 justify-start w-full">
            <FormField
              control={form.control}
              name="name.first"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label">First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="First Name" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name.last"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label">Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Last Name" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label">Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Email" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <PhoneFormField
              control={form.control}
              name="phone"
              label="Phone Number"
              placeholder="+1 (555) 123-4567"
            />

            <FormField
              control={form.control}
              name="company.name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label">Business Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter business name (optional)"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || undefined)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company.email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label">Business Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter business email (optional)"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || undefined)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Marketing text messages */}
          <div className="col-span-2 mt-2">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="flex-1 mr-4">
                <Label
                  htmlFor="admin-marketing-opt-in"
                  className="text-sm font-medium cursor-pointer"
                >
                  Marketing offers only
                </Label>
                {/* The visible audit line follows the locked persisted-event structure. Its
                    fragments are hidden from assistive technology because the sr-only sentence
                    supplies the same complete phrase with an expanded month and punctuation. */}
                <p className="mt-2 text-xs">
                  <span className="sr-only">{consentSpokenPhrase}</span>
                  <span>
                    <span
                      aria-hidden="true"
                      className={`whitespace-nowrap font-medium ${savedOptIn ? 'text-green-600' : 'text-gray-500'}`}
                    >
                      {savedStatusLabel}
                    </span>
                    {consentContext && (
                      <span aria-hidden="true" className="text-muted-foreground">
                        {' · '}
                        {consentContext}
                      </span>
                    )}
                  </span>
                </p>
              </div>
              <Switch
                id="admin-marketing-opt-in"
                checked={form.watch('marketingSmsOptIn') ?? false}
                onCheckedChange={(checked) =>
                  // Dirty is what tells the save this consent was actually changed here.
                  form.setValue('marketingSmsOptIn', !!checked, { shouldDirty: true })
                }
                disabled={!customer}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button disabled={isPending || !customer} type="submit" className="text-white mt-6">
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
};
